<script setup lang="ts">
/**
 * Champs CB intégrés sur la page de don.
 *
 * Selon le `provider` passé en prop :
 *   - "stripe" (default) : container `#card-element` pour Stripe Elements
 *     (rendu par stripeCheckout.mountCardForm)
 *   - "paypal" : container `#paypal-cardfields-container` pour PayPal
 *     CardFields ACDC (rendu par paypalCheckout.mountCardForm)
 *
 * L'UI extérieure (titre, bouton de test, conteneur stylé) reste identique
 * pour les deux providers — seul le container interne change. Cela garantit
 * une UX donateur cohérente quel que soit le provider de l'asso.
 *
 * Note technique : pour PayPal, le SDK rend 4 sous-iframes (Name, Number,
 * Expiry, CVV) à l'intérieur du container `#paypal-cardfields-container`
 * qui sont ensuite stylés via `style.input` côté PaypalProvider.
 */
const props = withDefaults(
  defineProps<{
    isDev?: boolean;
    isInitialized?: boolean;
    isPreviewMode?: boolean;
    /** Provider du formulaire CB. Default "stripe" pour compat ascendante. */
    provider?: "stripe" | "paypal";
    /**
     * Code d'erreur de mount carte. Si vaut `PAYPAL_CARDFIELDS_NOT_ELIGIBLE`,
     * on affiche un fallback "Pour payer par carte, cliquez sur PayPal et
     * choisissez Payer par carte sans compte" au lieu de 4 iframes vides.
     * Renvoyé par le PaypalProvider quand l'org n'a pas la capability
     * `PPCP_CUSTOM` activée (ACDC).
     */
    mountErrorCode?: string | null;
    /**
     * Message d'erreur affiché sous le bloc CB (équivalent du `#card-errors`
     * Stripe mais pour PayPal CardFields qui n'a pas de container natif).
     * La page le passe quand `submit` a échoué pour cause de CardFields vides
     * ou invalides. Pattern UX identique aux erreurs email/nom sous les inputs.
     */
    cardError?: string | null;
  }>(),
  {
    provider: "stripe",
    mountErrorCode: null,
    cardError: null,
  },
);

const { t } = useI18n();

const isPaypalCardFieldsUnavailable = computed(
  () =>
    props.provider === "paypal" &&
    props.mountErrorCode === "PAYPAL_CARDFIELDS_NOT_ELIGIBLE",
);

const emit = defineEmits<{
  copyTestCard: [];
}>();

const showTestCardInfo = ref(false);

const copyTestCard = async () => {
  try {
    await navigator.clipboard.writeText("4242424242424242");
    showTestCardInfo.value = true;
    setTimeout(() => {
      showTestCardInfo.value = false;
    }, 5000);
    emit("copyTestCard");
  } catch {
    // Copie non supportée
  }
};
</script>

<template>
  <div class="mb-6">
    <div class="flex items-center justify-between mb-3">
      <h4 class="text-xs font-medium text-gray-700 dark:text-gray-200">
        {{ t('don.cbIntegree.titre') }}
      </h4>

      <!-- Bouton de test (mode dev uniquement) -->
      <button
        v-if="isDev"
        type="button"
        class="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
        :title="t('ui.carteTest.titreBouton')"
        @click="copyTestCard"
      >
        {{ t('ui.carteTest.bouton') }}
      </button>
    </div>

    <!-- Message de test (visible après clic) -->
    <div
      v-if="showTestCardInfo"
      class="mb-2 p-3 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded text-xs text-indigo-800 dark:text-indigo-300"
    >
      <p class="font-semibold">{{ t('ui.carteTest.copiee') }}</p>
      <p class="mt-1">
        <strong>{{ t('ui.carteTest.numero') }}</strong> 4242 4242 4242 4242<br />
        <strong>{{ t('ui.carteTest.date') }}</strong> 12/34<br />
        <strong>{{ t('ui.carteTest.cvc') }}</strong> 123
      </p>
    </div>

    <!-- Container stylisé pour Stripe Elements / PayPal CardFields.
         Même cadre extérieur pour les deux providers (border, rounded, shadow).
         Padding réduit pour PayPal car les iframes ont déjà leur padding interne.
         Bordure rouge si cardError présent — UX cohérente avec inputs invalides. -->
    <div
      :class="[
        'border rounded-lg shadow-[0_0_5px_rgba(0,0,0,0.1)] bg-white dark:bg-gray-800 transition-shadow',
        cardError
          ? 'border-red-500 dark:border-red-400 shadow-[0_0_0_3px_rgba(239,68,68,0.15)]'
          : 'border-gray-200 dark:border-gray-700',
        provider === 'paypal' && !isPaypalCardFieldsUnavailable && !isPreviewMode
          ? 'px-2 py-0 focus-within:border-main focus-within:shadow-[0_0_0_3px_rgba(31,154,254,0.15)]'
          : 'p-4',
      ]"
    >
      <!-- Mode aperçu - placeholder style Stripe -->
      <div v-if="isPreviewMode">
        <!-- Ligne numéro de carte -->
        <div class="flex items-center border-b border-gray-200 pb-3 mb-3">
          <div class="flex items-center gap-2 mr-3">
            <!-- Icône carte -->
            <svg class="w-8 h-5 text-gray-300" viewBox="0 0 32 21" fill="currentColor">
              <rect width="32" height="21" rx="3" fill="#E5E7EB"/>
              <rect x="3" y="4" width="8" height="5" rx="1" fill="#9CA3AF"/>
              <rect x="3" y="12" width="12" height="2" rx="1" fill="#D1D5DB"/>
              <rect x="3" y="16" width="8" height="2" rx="1" fill="#D1D5DB"/>
            </svg>
          </div>
          <span class="text-sm text-gray-400 flex-1">{{ t('don.cbIntegree.previewNumeroCarte') }}</span>
          <div class="flex items-center gap-1.5">
            <span class="px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded">{{ t('don.cbIntegree.previewPreremplir') }}</span>
            <span class="text-[10px] font-semibold text-green-600">link</span>
          </div>
        </div>
        <!-- Ligne date et CVC -->
        <div class="flex gap-4">
          <span class="text-sm text-gray-400 flex-1">{{ t('don.cbIntegree.previewMoisAnnee') }}</span>
          <span class="text-sm text-gray-400 flex-1">CVC</span>
        </div>
      </div>

      <!-- Mode normal (pas preview) -->
      <template v-else>
        <!-- PayPal CardFields non éligible (capability PPCP_CUSTOM absente)
             → fallback message clair pour rediriger vers le bouton PayPal.
             Évite l'affichage de 4 iframes vides confusants. -->
        <div
          v-if="isPaypalCardFieldsUnavailable"
          class="flex items-start gap-3 py-3"
        >
          <UIcon
            name="i-lucide-info"
            class="size-5 text-blue-500 shrink-0 mt-0.5"
          />
          <i18n-t
            keypath="don.cbIntegree.paypalIndisponible"
            tag="p"
            class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed"
          >
            <template #paypal>
              <span class="font-semibold text-[#003087] dark:text-[#0070BA]">PayPal</span>
            </template>
            <template #mention>
              <span class="font-medium">{{ t('don.cbIntegree.paypalIndisponibleMention') }}</span>
            </template>
          </i18n-t>
        </div>

        <!-- Cas nominal : Stripe Elements OU PayPal CardFields.
             ⚠️ NE PAS METTRE `#card-element` EN `v-else-if` du loader :
             `mountCardForm("card-element")` poll le DOM 5s à la recherche
             de `#card-element`, et l'élément doit donc être PRÉSENT pendant
             ce poll. Le loader s'affiche EN OVERLAY par-dessus le container
             vide, pas À LA PLACE. Avant fix : le loader cachait le container,
             `waitForElement` timeout → "Element #card-element not found in
             DOM after 5000ms". Cf. commit 96f725d3d (fix initial), d3dc9fe78
             (régression), `pages/b/[slug]/index.vue:1100` (mount call). -->
        <div v-else class="relative">
          <!-- Container Stripe Elements (TOUJOURS dans le DOM, en flow
               normal pour que sa hauteur s'adapte aux iframes Stripe). -->
          <div
            v-if="provider === 'stripe'"
            id="card-element"
            class="min-h-[24px]"
          />
          <!-- Container PayPal CardFields (TOUJOURS dans le DOM).
               Hauteur ajustée à ~52px car le SDK PayPal v6 rend les 3 fields
               (Numéro, MM/AA, CVC) sur UNE SEULE LIGNE — le NameField est
               omis (capturé en amont via DonateDonorForm) et fait gagner une
               ligne. Avant : min-h:180px historique (4 fields verticaux) →
               ~130px de vide visible sous la ligne carte. Karim 2026-06-27. -->
          <div
            v-else
            id="paypal-cardfields-container"
            class="min-h-[52px]"
          />

          <!-- Loader overlay par-dessus le container vide tant que le SDK
               n'a pas terminé son init (iframes pas encore rendues). -->
          <div
            v-if="!isInitialized"
            class="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800"
          >
            <UIcon
              name="i-lucide-loader-2"
              class="size-5 animate-spin text-gray-400"
            />
            <span class="ml-2 text-sm text-gray-500 dark:text-gray-400">
              {{ t('don.cbIntegree.chargement') }}
            </span>
          </div>
        </div>
      </template>

      <!-- Container pour les erreurs de carte (Stripe uniquement — PayPal
           gère ses erreurs via onError du SDK). -->
      <div v-if="provider === 'stripe'" id="card-errors" class="text-red-500 text-sm" />
    </div>

    <!-- Erreur carte (Stripe ou PayPal) — affichée sous le bloc CB après
         tentative de submit refusée. Même pattern visuel que les erreurs
         email/nom dans DonateDonorForm pour cohérence UX. -->
    <div
      v-if="cardError"
      class="mt-2 text-xs text-red-600 dark:text-red-400 flex items-start gap-1"
    >
      <UIcon name="i-lucide-alert-circle" class="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
      <span>{{ cardError }}</span>
    </div>

    <p
      v-if="!isPaypalCardFieldsUnavailable"
      class="mt-2 text-xs text-gray-500 dark:text-gray-400"
    >
      {{ t('don.cbIntegree.securiseParIntro') }}
      <span v-if="provider === 'paypal'">PayPal</span>
      <span v-else>Stripe</span>
    </p>
  </div>
</template>
